import HttpClient, { FetchRequestConfig } from './HttpClient';
import HTTPResponse, { ErrorResponse } from './HttpResponse';
import { SparePart, CartItems, PartIdCount } from '../interfaces/Item';
import { CartRecipient } from '../interfaces/Cart';

interface CountryAndLanguage {
  country: string;
  language: string;
}
class CartService {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  public async createCart(): Promise<string> {
    const { error, result } = await this.http.post(`cart/create`, {});
    if (error) {
      throw error;
    }
    return result.cartId;
  }

  async _postCartItemsWithResponseError(
    url: string,
    data: {}
  ): Promise<HTTPResponse<CartItems>> {
    const { error, result } = await this.http.post(url, data);
    if (error) {
      throw error;
    }
    return result.cartItems;
  }

  async postCartItemsWithResponseError(
    url: string,
    data: {}
  ): Promise<HTTPResponse<CartItems>> {
    const response = await fetch(this.http.formatUrl(url), {
      ...(this.http.getHeader('POST') as FetchRequestConfig),
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.status < 200 || response.status >= 300) {
      const message = await response.json();
      const error: ErrorResponse = {
        status: response.status,
        data: {
          message,
        },
      };
      return new HTTPResponse(
        {
          cartItems: [],
        },
        error
      );
    }

    const result = await response.json();

    return new HTTPResponse(result);
  }

  public async editItem(
    cartId: string,
    item: SparePart,
    quantity: number
  ): Promise<PartIdCount[]> {
    const { error, result } = await this.postCartItemsWithResponseError(
      `cart/edit`,
      {
        partId: item.id,
        quantity,
        cart: {
          cartId,
        },
      }
    );
    if (error) {
      throw error;
    }
    return result.cartItems;
  }

  public async addToCart(
    cartId: string,
    item: SparePart,
    quantity: number
  ): Promise<PartIdCount[]> {
    const { error, result } = await this.postCartItemsWithResponseError(
      `cart/add`,
      {
        partId: item.id,
        quantity,
        cart: {
          cartId,
        },
      }
    );

    if (error) {
      throw error;
    }

    return result.cartItems;
  }

  public async removeFromCart(
    cartId: string,
    item: SparePart
  ): Promise<CartItems> {
    const { error, result } = await this.http.post(`cart/delete`, {
      partId: item.id,
      cart: {
        cartId,
      },
    });
    if (error) {
      throw error;
    }

    return result;
  }

  public async orderCart(
    cartId: string,
    customerData: CartRecipient & CountryAndLanguage,
    turnstileToken: string
  ): Promise<HTTPResponse<void>> {
    const { error, result } = await this.http.post(
      `order/${cartId}`,
      customerData,
      turnstileToken
    );

    if (error) {
      throw error;
    }
    return result;
  }

  public async getItems(cartId: string): Promise<PartIdCount[]> {
    const { error, result } = await this.http.get(`cart/get/${cartId}`);
    if (error) {
      throw error;
    }
    return result.cartItems;
  }
}

export const cartService = new CartService(new HttpClient());
